import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Database,
  CheckCircle2,
  AlertCircle,
  Clock,
  FileCode,
  GitBranch,
  Rocket,
  Server,
  Table,
  BookOpen,
} from "lucide-react";
import { invoicePrompt } from "@/data/modulePrompts";
import { ModulePromptTab } from "@/components/documentation/ModulePromptTab";



// Production-ready sections
const productionSections = [
  {
    id: "invoice-crud",
    name: "Invoice CRUD",
    status: "production",
    dbTables: ["acc_customer_invoices"],
    description: "Create, read, update via accountingService.ts",
    file: "src/services/accountingService.ts",
  },
  {
    id: "invoice-number-gen",
    name: "Invoice Number Generation",
    status: "production",
    dbTables: ["acc_customer_invoices"],
    description: "Auto-generates sequential invoice numbers (INVYYxxxxx)",
    file: "src/services/accountingService.ts",
  },
  {
    id: "invoice-line-items",
    name: "Invoice Line Items",
    status: "production",
    dbTables: ["acc_invoice_lines"],
    description: "Charge lines with quantity, unit price, tax, ROE per line",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "customer-search",
    name: "Customer Search",
    status: "production",
    dbTables: ["mst_parties"],
    description: "Type-ahead customer selection from parties master",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "charge-code-lookup",
    name: "Charge Code Lookup",
    status: "production",
    dbTables: ["mst_charge_codes"],
    description: "Charge code dropdown with auto-fill basis field",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "port-linking",
    name: "Port/Location Linking",
    status: "production",
    dbTables: ["mst_ports"],
    description: "Origin and destination port references for shipment context",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "booking-linking",
    name: "Booking Linking",
    status: "production",
    dbTables: ["exp_bookings"],
    description: "Link invoice to shipment booking for job costing context",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "currency-support",
    name: "Currency Support",
    status: "production",
    dbTables: ["mst_currencies"],
    description: "Multi-currency with ROE (rate of exchange) per line",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "metadata-jsonb",
    name: "Metadata (JSONB)",
    status: "production",
    dbTables: ["acc_customer_invoices"],
    description: "Supplemental fields (division, vessel, weight, etc.) stored in metadata column",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "payment-toggles",
    name: "Payment Toggle Options",
    status: "production",
    dbTables: ["acc_customer_invoices"],
    description: "Wire, Zelle, Credit Card display toggles saved in metadata",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "print-functionality",
    name: "Print Functionality",
    status: "production",
    dbTables: [],
    description: "Client-side window.print() triggered from action toolbar",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "action-toolbar",
    name: "Action Toolbar",
    status: "production",
    dbTables: [],
    description: "Save, Print, Email, Tech Guide, Add New, Invoice Log buttons with gradient styling",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "two-panel-layout",
    name: "Two-Panel Layout",
    status: "production",
    dbTables: [],
    description: "Left: shipment/reference details, Right: admin/dates/metrics",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "card-charge-lines",
    name: "Card-Based Charge Lines",
    status: "production",
    dbTables: ["acc_invoice_lines"],
    description: "Card UI with grouped fields, larger click targets (h-8) for usability",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
  },
  {
    id: "payment-application",
    name: "Payment Application",
    status: "production",
    dbTables: ["acc_payments", "acc_payment_applications"],
    description: "Full payment UI with application grid linking payments to invoices/bills, auto-updating balance_due and status",
    file: "src/services/paymentService.ts",
  },
  {
    id: "payment-receipts",
    name: "Customer Receipts & Vendor Payments",
    status: "production",
    dbTables: ["acc_payments"],
    description: "Tabbed list page with summary cards, status pills, staggered animations, and colorful theme support",
    file: "src/pages/Payments.tsx",
  },
  {
    id: "gl-journal-entries",
    name: "GL Journal Entries",
    status: "production",
    dbTables: ["acc_gl_journals", "acc_gl_journal_lines"],
    description: "Double-entry journal CRUD with balanced debit/credit validation, posting, and reversal",
    file: "src/services/glJournalService.ts",
  },
  {
    id: "journal-entry-form",
    name: "Journal Entry Form",
    status: "production",
    dbTables: ["acc_gl_journals", "acc_gl_journal_lines", "acc_chart_of_accounts"],
    description: "Dynamic line items grid with color-coded debit/credit columns, balance indicator, and account lookup",
    file: "src/components/accounting/JournalEntryFormDialog.tsx",
  },
  {
    id: "journal-post-reverse",
    name: "Journal Post & Reverse",
    status: "production",
    dbTables: ["acc_gl_journals"],
    description: "Post draft journals, reverse posted journals with auto-generated counter-entries",
    file: "src/services/glJournalService.ts",
  },
  {
    id: "visual-theme-accounting",
    name: "Colorful Theme Support",
    status: "production",
    dbTables: [],
    description: "Gradient headers, animated summary cards, staggered row animations, and VisualThemeSelector on Payments & Journals pages",
    file: "src/pages/Payments.tsx",
  },
];

// Mock/partial sections
const mockSections = [
  {
    id: "email-sending",
    name: "Email Sending",
    status: "mock",
    dbTables: [],
    description: "Currently shows toast placeholder — no actual email integration",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
    missingFeatures: [
      "Edge function integration (Resend/SendGrid)",
      "Email templates with invoice layout",
      "Delivery tracking and status",
    ],
  },
  {
    id: "pdf-generation",
    name: "PDF Invoice Generation",
    status: "mock",
    dbTables: [],
    description: "No server-side PDF generation — relies on window.print()",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
    missingFeatures: [
      "Server-side PDF generation via edge function",
      "Branded invoice template system",
      "PDF attachment to email",
    ],
  },
  {
    id: "job-costing-prefill",
    name: "Job Costing Pre-fill",
    status: "partial",
    dbTables: ["exp_bookings", "exp_booking_charges"],
    description: "Booking can be linked but charges are not auto-populated from booking",
    file: "src/components/accounting/CustomerInvoiceFormDialog.tsx",
    missingFeatures: [
      "Full AR/AP charge sync from booking",
      "Auto-populate invoice lines from booking charges",
      "Profit margin calculation",
    ],
  },
];

// Changelog entries
const changelog = [
  {
    version: "1.3.0",
    date: "2026-02-15",
    status: "released",
    changes: [
      "GL Journal Entries: full CRUD with double-entry validation (debit = credit)",
      "Journal posting & reversal with auto-generated counter-entries",
      "Payment Application UI: link payments to outstanding invoices/bills",
      "Auto-update balance_due and status (paid/partially_paid) on application",
      "Customer Receipts & Vendor Payments tabbed list page",
      "Gradient headers, animated summary cards, staggered row animations",
      "Color-coded debit/credit columns in journal entry form",
      "Payment method badges, status dot indicators, progress bar for applied %",
      "VisualThemeSelector (Standard/Colorful) on Payments & Journals pages",
      "Sectioned payment form with themed cards (Payment Details, Banking, Notes)",
    ],
  },
  {
    version: "1.2.0",
    date: "2026-02-14",
    status: "released",
    changes: [
      "Two-panel layout reorganization (shipment left, admin right)",
      "Action toolbar with Save, Print, Email, Tech Guide, Add New, Invoice Log",
      "Section color accents (teal, amber, purple) with themed icons",
      "Description textarea for invoice-level notes",
      "Technical Help Dialog with 6-tab guide (this dialog)",
    ],
  },
  {
    version: "1.1.0",
    date: "2026-02-10",
    status: "released",
    changes: [
      "Card-based charge line UI replacing dense table layout",
      "Metadata JSONB column for supplemental fields (division, vessel, weight, etc.)",
      "Payment display toggles (Wire, Zelle, Credit Card)",
      "Charge code auto-fill with basis field",
    ],
  },
  {
    version: "1.0.0",
    date: "2026-01-28",
    status: "released",
    changes: [
      "Initial invoice CRUD via accountingService.ts",
      "Sequential invoice number generation (INVYYxxxxx)",
      "Invoice line items with quantity, unit price, tax",
      "Customer selection from mst_parties",
      "Origin/destination port linking",
      "Booking linking for shipment context",
      "Multi-currency support with ROE per line",
    ],
  },
  {
    version: "1.4.0",
    date: "2026-03-15",
    status: "upcoming",
    changes: [
      "Email invoice via edge function",
      "PDF invoice generation",
      "Job costing pre-fill from booking charges",
      "Aging report integration",
    ],
  },
];

// Roadmap items
const roadmapItems = [
  {
    title: "Email Invoice via Edge Function",
    description: "Send invoices via email using Resend API with branded templates",
    priority: "high",
    targetVersion: "1.4.0",
  },
  {
    title: "PDF Invoice Generation",
    description: "Server-side PDF rendering with company branding and line item formatting",
    priority: "high",
    targetVersion: "1.4.0",
  },
  {
    title: "Aging Report Integration",
    description: "Show invoice aging (current, 30, 60, 90+ days) on dashboard and reports",
    priority: "medium",
    targetVersion: "1.4.0",
  },
  {
    title: "Recurring Invoices",
    description: "Schedule automatic invoice generation for recurring charges",
    priority: "low",
    targetVersion: "1.5.0",
  },
  {
    title: "Bank Reconciliation",
    description: "Match payments against bank statement imports for reconciliation",
    priority: "medium",
    targetVersion: "1.5.0",
  },
  {
    title: "Trial Balance & Financial Statements",
    description: "Generate trial balance, income statement, and balance sheet from GL journal data",
    priority: "high",
    targetVersion: "1.5.0",
  },
];

export function InvoiceTechnicalHelpDialog({
  open,
  onOpenChange,
}) {
  const [activeTab, setActiveTab] = useState("overview");

  const totalSections = productionSections.length + mockSections.length;
  const productionCount = productionSections.length;
  const completionPercent = Math.round((productionCount / totalSections) * 100);

  const allTables = [
    ...new Set([
      ...productionSections.flatMap((s) => s.dbTables),
      ...mockSections.flatMap((s) => s.dbTables),
    ]),
  ].filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-primary" />
            Customer Invoice - Technical Help Guide
          </DialogTitle>
          <DialogDescription>
            Database integration status, mock data locations, and development roadmap
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="db-status">DB Status</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="changelog">Changelog</TabsTrigger>
            <TabsTrigger value="roadmap">Roadmap</TabsTrigger>
            <TabsTrigger value="architecture">Architecture</TabsTrigger>
            <TabsTrigger value="prompt" className="gap-1"><BookOpen className="h-3 w-3" />Prompt</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[55vh] mt-4 pr-4">
            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Module Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Production Readiness
                    </span>
                    <span className="text-2xl font-bold text-primary">
                      {completionPercent}%
                    </span>
                  </div>
                  <Progress value={completionPercent} className="h-3" />
                  <div className="grid grid-cols-3 gap-4 pt-2">
                    <div className="text-center p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {productionCount}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Production Ready
                      </div>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">
                        {mockSections.filter((s) => s.status === "partial").length}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Partial
                      </div>
                    </div>
                    <div className="text-center p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">
                        {mockSections.filter((s) => s.status === "mock").length}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Mock Data
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Database Tables Used
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {allTables.map((table) => (
                      <Badge key={table} variant="outline" className="font-mono text-xs">
                        <Table className="h-3 w-3 mr-1" />
                        {table}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileCode className="h-4 w-4" />
                    Key Files
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-xs">
                        src/components/accounting/CustomerInvoiceFormDialog.tsx
                      </code>
                      <span className="text-muted-foreground">Main invoice form</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-xs">
                        src/services/accountingService.ts
                      </code>
                      <span className="text-muted-foreground">Invoice CRUD service</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-xs">
                        src/services/glJournalService.ts
                      </code>
                      <span className="text-muted-foreground">GL Journal CRUD, post, reverse</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-xs">
                        src/services/paymentService.ts
                      </code>
                      <span className="text-muted-foreground">Payment creation, application, balance updates</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-xs">
                        src/pages/Payments.tsx
                      </code>
                      <span className="text-muted-foreground">Receipts & Payments list page</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-xs">
                        src/pages/JournalEntries.tsx
                      </code>
                      <span className="text-muted-foreground">Journal Entries list page</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-xs">
                        src/components/accounting/PaymentFormDialog.tsx
                      </code>
                      <span className="text-muted-foreground">Payment form with application grid</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-xs">
                        src/components/accounting/JournalEntryFormDialog.tsx
                      </code>
                      <span className="text-muted-foreground">Journal entry form with line items</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-xs">
                        src/components/accounting/InvoiceTechnicalHelpDialog.tsx
                      </code>
                      <span className="text-muted-foreground">This tech guide dialog</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* DB Status Tab */}
            <TabsContent value="db-status" className="space-y-3">
              {productionSections.map((section) => (
                <Card key={section.id}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <h4 className="font-medium">{section.name}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {section.description}
                        </p>
                        {section.dbTables.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {section.dbTables.map((table) => (
                              <Badge
                                key={table}
                                variant="secondary"
                                className="text-xs font-mono"
                              >
                                {table}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        Production
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* Pending Tab */}
            <TabsContent value="pending" className="space-y-3">
              {mockSections.map((section) => (
                <Card key={section.id}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {section.status === "mock" ? (
                            <AlertCircle className="h-4 w-4 text-orange-600" />
                          ) : (
                            <Clock className="h-4 w-4 text-yellow-600" />
                          )}
                          <h4 className="font-medium">{section.name}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {section.description}
                        </p>
                        {section.file && (
                          <code className="text-xs bg-muted px-2 py-0.5 rounded mt-2 inline-block">
                            {section.file}
                          </code>
                        )}
                        {section.missingFeatures && (
                          <div className="mt-3 space-y-1">
                            <span className="text-xs font-medium text-muted-foreground">
                              Missing Features:
                            </span>
                            <ul className="text-xs space-y-1">
                              {section.missingFeatures.map((feature, i) => (
                                <li key={i} className="flex items-center gap-2">
                                  <span className="w-1 h-1 bg-orange-500 rounded-full" />
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <Badge
                        className={
                          section.status === "mock"
                            ? "bg-orange-100 text-orange-700 border-orange-200"
                            : "bg-yellow-100 text-yellow-700 border-yellow-200"
                        }
                      >
                        {section.status === "mock" ? "Mock Data" : "Partial"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* Changelog Tab */}
            <TabsContent value="changelog" className="space-y-4">
              {changelog.map((entry) => (
                <Card key={entry.version}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <GitBranch className="h-4 w-4" />
                        v{entry.version}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {entry.date}
                        </span>
                        <Badge
                          variant={entry.status === "released" ? "default" : "outline"}
                        >
                          {entry.status === "released" ? "Released" : "Upcoming"}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1">
                      {entry.changes.map((change, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                          {change}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* Roadmap Tab */}
            <TabsContent value="roadmap" className="space-y-3">
              {roadmapItems.map((item, index) => (
                <Card key={index}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Rocket className="h-4 w-4 text-primary" />
                          <h4 className="font-medium">{item.title}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {item.description}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            Target: v{item.targetVersion}
                          </Badge>
                        </div>
                      </div>
                      <Badge
                        className={
                          item.priority === "high"
                            ? "bg-red-100 text-red-700 border-red-200"
                            : item.priority === "medium"
                            ? "bg-blue-100 text-blue-700 border-blue-200"
                            : "bg-gray-100 text-gray-700 border-gray-200"
                        }
                      >
                        {item.priority}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* Architecture Tab */}
            <TabsContent value="architecture" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Server className="h-4 w-4" />
                    Invoice Data Flow
                  </CardTitle>
                  <CardDescription>
                    How invoice data flows from the form to the database
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">Save Flow</h4>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <Badge variant="outline" className="font-mono">InvoiceFormDialog</Badge>
                      <span className="text-muted-foreground">validates →</span>
                      <Badge variant="outline" className="font-mono">accountingService.ts</Badge>
                      <span className="text-muted-foreground">inserts →</span>
                      <Badge variant="outline" className="font-mono">acc_customer_invoices</Badge>
                      <span className="text-muted-foreground">+ lines →</span>
                      <Badge variant="outline" className="font-mono">acc_invoice_lines</Badge>
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    <h4 className="font-medium text-sm">Payment Application Flow</h4>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <Badge variant="outline" className="font-mono">PaymentFormDialog</Badge>
                      <span className="text-muted-foreground">→</span>
                      <Badge variant="outline" className="font-mono">paymentService.ts</Badge>
                      <span className="text-muted-foreground">inserts →</span>
                      <Badge variant="outline" className="font-mono">acc_payments</Badge>
                      <span className="text-muted-foreground">+ apps →</span>
                      <Badge variant="outline" className="font-mono">acc_payment_applications</Badge>
                      <span className="text-muted-foreground">→ updates</span>
                      <Badge variant="outline" className="font-mono">invoice.balance_due</Badge>
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    <h4 className="font-medium text-sm">GL Journal Entry Flow</h4>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <Badge variant="outline" className="font-mono">JournalEntryFormDialog</Badge>
                      <span className="text-muted-foreground">validates (debit=credit) →</span>
                      <Badge variant="outline" className="font-mono">glJournalService.ts</Badge>
                      <span className="text-muted-foreground">inserts →</span>
                      <Badge variant="outline" className="font-mono">acc_gl_journals</Badge>
                      <span className="text-muted-foreground">+ lines →</span>
                      <Badge variant="outline" className="font-mono">acc_gl_journal_lines</Badge>
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    <h4 className="font-medium text-sm">Journal Reversal Flow</h4>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <Badge variant="outline" className="font-mono">reverseJournal(id)</Badge>
                      <span className="text-muted-foreground">→ creates new journal with swapped debit/credit →</span>
                      <Badge variant="outline" className="font-mono">marks original reversed=true</Badge>
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    <h4 className="font-medium text-sm">Metadata JSONB Structure</h4>
                    <div className="bg-muted rounded-lg p-3 text-xs font-mono space-y-1">
                      <div>{"{"}</div>
                      <div className="pl-4">"division": "string",</div>
                      <div className="pl-4">"ref_no": "string",</div>
                      <div className="pl-4">"vessel_voyage": "string",</div>
                      <div className="pl-4">"weight": "string",</div>
                      <div className="pl-4">"measurement": "string",</div>
                      <div className="pl-4">"profit_center": "string",</div>
                      <div className="pl-4">"description": "string",</div>
                      <div className="pl-4">"show_wire": "boolean",</div>
                      <div className="pl-4">"show_zelle": "boolean",</div>
                      <div className="pl-4">"show_credit_card": "boolean"</div>
                      <div>{"}"}</div>
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    <h4 className="font-medium text-sm">Charge Code Auto-fill Flow</h4>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <Badge variant="outline" className="font-mono">Select charge code</Badge>
                      <span className="text-muted-foreground">→ lookup</span>
                      <Badge variant="outline" className="font-mono">mst_charge_codes</Badge>
                      <span className="text-muted-foreground">→ auto-fill</span>
                      <Badge variant="outline" className="font-mono">description + basis</Badge>
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-3 text-sm">
                    <h4 className="font-medium text-sm">Key Database Tables</h4>
                    <div className="grid grid-cols-[180px_1fr] gap-2">
                      <span className="font-medium text-muted-foreground font-mono text-xs">acc_customer_invoices</span>
                      <span className="text-xs">Invoice header: dates, customer, ports, totals, status, metadata JSONB</span>
                    </div>
                    <div className="grid grid-cols-[180px_1fr] gap-2">
                      <span className="font-medium text-muted-foreground font-mono text-xs">acc_invoice_lines</span>
                      <span className="text-xs">Line items: charge code, qty, price, tax, ROE, GL account</span>
                    </div>
                    <div className="grid grid-cols-[180px_1fr] gap-2">
                      <span className="font-medium text-muted-foreground font-mono text-xs">acc_payments</span>
                      <span className="text-xs">Payment records linked to parties with method, bank account, amounts</span>
                    </div>
                    <div className="grid grid-cols-[180px_1fr] gap-2">
                      <span className="font-medium text-muted-foreground font-mono text-xs">acc_payment_applications</span>
                      <span className="text-xs">Links payments to invoices/bills with applied_amount per document</span>
                    </div>
                    <div className="grid grid-cols-[180px_1fr] gap-2">
                      <span className="font-medium text-muted-foreground font-mono text-xs">acc_gl_journals</span>
                      <span className="text-xs">Journal headers: date, type, totals, post/reverse status</span>
                    </div>
                    <div className="grid grid-cols-[180px_1fr] gap-2">
                      <span className="font-medium text-muted-foreground font-mono text-xs">acc_gl_journal_lines</span>
                      <span className="text-xs">Journal line items: account, debit, credit, customer/vendor refs</span>
                    </div>
                    <div className="grid grid-cols-[180px_1fr] gap-2">
                      <span className="font-medium text-muted-foreground font-mono text-xs">acc_chart_of_accounts</span>
                      <span className="text-xs">GL accounts for revenue/expense posting and bank accounts</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="prompt" className="mt-0">
              <ModulePromptTab data={invoicePrompt} />
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
